import { useAuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, Checkbox, ErrorMessage, Input } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { ClientOnly } from '@tanstack/react-router';
import { MouseEvent, Suspense, lazy, useEffect, useId, useRef, useState } from 'react';
import { CountryDropdown } from 'react-country-region-selector';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePasswordValidator, useValidations, useSubmitValidation } from '../../..';
import { AccountWizardState } from '../../../types';
import { PasswordEyeIcon, TermsAndCondition } from '../../atoms';
import type { AltchaWidgetHandle } from '../AltchaWidget';

const AltchaWidget = lazy(() => import('../AltchaWidget'));

interface Props {
    emailAddress?: string;
    quickRegister?: boolean;
    onAuthorized?: () => void;
    onBack: () => void;
}

export const Register = ({ emailAddress, quickRegister, onAuthorized, onBack }: Props) => {
    const { t } = useTranslation();
    const {
        register,
        formState: { errors },
        getValues,
        getFieldState,
        handleSubmit,
        trigger,
        setValue,
        watch
    } = useForm<AccountWizardState>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: { emailAddress } });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<AccountWizardState> = () => {
        doRegister({ preventDefault: () => {}, stopPropagation: () => {} } as MouseEvent<HTMLButtonElement>);
    };

    const altchaRef = useRef<AltchaWidgetHandle>(null);
    const [emailExists, setEmailExists] = useState(false);
    const [passwordType, setPasswordType] = useState('password');
    const [passwordScore, setPasswordScore] = useState(-1);

    const { updateAuthState } = useAuthContext();
    const isHydrated = useIsHydrated();
    const { validatePassword, passwordValidation, passwordMessages } = usePasswordValidator();
    const { emailValidation, firstNameValidation, lastNameValidation, nicknameValidation, companyNameValidation, termsOfAgreementValidation } =
        useValidations();

    const togglePasswordVisibility = () => setPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));

    const countryId = useId();

    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const countryCode = watch('countryCode');
    useEffect(() => {
        const nickNameState = getFieldState('nickname');
        if (nickNameState.isDirty) return;
        const nickname = `${firstName ?? ''} ${lastName ?? ''}`.trim();
        setValue('nickname', nickname, { shouldDirty: false, shouldTouch: true });
    }, [firstName, lastName]);

    const password = watch('newPassword');
    useEffect(() => {
        if (password) {
            validatePassword(password).then((result) => setPasswordScore(result.score));
        } else {
            setPasswordScore(-1);
        }
    }, [password]);

    const emailAddressWatch = watch('emailAddress');
    const validateEmailMutation = useMutation(OptionsApi.login.exists());
    const checkEmailExists = (email: string) => {
        validateEmailMutation.mutate(email, {
            onSuccess: (response) => setEmailExists(response),
            onError: () => setEmailExists(false)
        });
    };
    useEffect(() => {
        if (!emailAddressWatch) return;
        const handle = setTimeout(() => checkEmailExists(emailAddressWatch), 1000);
        return () => clearTimeout(handle);
    }, [emailAddressWatch]);

    const registerMutation = useMutation(OptionsApi.login.register());
    const doRegister = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (registerMutation.isPending) return;

        // In the order of the form: validate focuses the first invalid field in the order of this list
        const fields = (
            quickRegister
                ? ['nickname', 'emailAddress', 'newPassword', 'termsOfAgreement']
                : ['firstName', 'lastName', 'nickname', 'companyName', 'emailAddress', 'newPassword', 'termsOfAgreement']
        ) as Array<keyof AccountWizardState>;

        setValue('captcha', altchaRef.current?.value);

        // All fields at once, so every error shows; focus (and so scroll) goes to the first invalid field
        validate(fields).then((isValid) => {
            if (!isValid) return;
            let values = getValues();
            values = { ...values, password: values.newPassword };
            registerMutation.mutate(values, {
                onSuccess: (response) => {
                    updateAuthState(response);
                    if (onAuthorized) onAuthorized();
                },
                onError: () => {
                    const email = getValues('emailAddress');
                    if (email) checkEmailExists(email);
                },
                onSettled: () => altchaRef.current?.reset()
            });
        });
    };

    const handleBack = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        onBack();
    };

    const error = validateEmailMutation.error ?? registerMutation.error;
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {error && <ErrorMessage errorSubject="Account.ApiErrorCode" apiError={error} classNames="mb-4" />}
            {!quickRegister && (
                <Input
                    autoFocus
                    label={t('Account.Label.FirstName')}
                    {...register('firstName', firstNameValidation)}
                    errorMessage={errors.firstName?.message}
                    autoComplete="given-name"
                />
            )}
            {!quickRegister && (
                <Input
                    label={t('Account.Label.LastName')}
                    {...register('lastName', lastNameValidation)}
                    errorMessage={errors.lastName?.message}
                    autoComplete="family-name"
                />
            )}
            <Input
                autoFocus={quickRegister}
                label={t('Account.Label.Nickname')}
                {...register('nickname', nicknameValidation)}
                errorMessage={errors.nickname?.message}
                autoComplete="nickname"
            />
            {!quickRegister && (
                <Input
                    label={t('Account.Label.CompanyName')}
                    {...register('companyName', companyNameValidation)}
                    errorMessage={errors.companyName?.message}
                    autoComplete="organization"
                />
            )}
            {!quickRegister && (
                <div className="group relative w-full clear-both mb-10">
                    <label htmlFor={countryId} className="block text-sm">{t('Account.Label.Country')}</label>
                    <div className="flex justify-between">
                        <CountryDropdown
                            id={countryId}
                            className="w-full bg-transparent outline-hidden py-input-vert text-base"
                            value={countryCode ?? ''}
                            onChange={(val) => setValue('countryCode', val)}
                            valueType="short"
                            autoComplete="country"
                        />
                    </div>
                    <div className={`bg-black group-hover:bg-primary group-focus-within:bg-primary input-border`} />
                </div>
            )}
            <Input
                type="email"
                label={t('Account.Label.Email')}
                {...register('emailAddress', emailValidation)}
                autoComplete="email"
                errorMessage={emailExists ? t('Account.ApiErrorCode.ConflictException') : errors.emailAddress?.message}
                // "Already in use" appears a second after typing stops, while the user is in the field
                announceMessage
            />
            <Input
                type={passwordType}
                label={t('Account.Label.Password')}
                {...register('newPassword', passwordValidation)}
                autoComplete="new-password"
                {...passwordMessages(passwordScore, errors.newPassword?.message)}
            >
                <PasswordEyeIcon passwordType={passwordType} onClick={togglePasswordVisibility} />
            </Input>
            <Checkbox
                id="termsOfAgreement"
                label={<TermsAndCondition />}
                errorMessage={errors.termsOfAgreement?.message}
                classNames="mb-10"
                autoComplete="off"
                {...register('termsOfAgreement', termsOfAgreementValidation)}
            />
            <div className="mb-10">
                {/* Skip the captcha widget entirely in E2E tests where Altcha isn't worth (or possible to) testing.
                    Backend pairs this with Altcha:BypassInTests=true. Off in normal builds.
                    The `as any` cast is because this shared package doesn't reference vite/client types;
                    Vite handles import.meta.env at build time regardless. */}
                {(import.meta as any).env?.VITE_SKIP_CAPTCHA === 'true' ? null : (
                    <ClientOnly>
                        <Suspense fallback={<div className="flex items-center justify-center bg-black/50 z-10">Loading...</div>}>
                            <AltchaWidget ref={altchaRef} />
                        </Suspense>
                    </ClientOnly>
                )}
            </div>
            <div className="clear-both flex flex-wrap gap-4 clear-both mb-8">
                <Button
                    type="button"
                    onClick={handleBack}
                    isLoading={registerMutation.isPending}
                    variant="primary-outline"
                    classNames="flex-1 whitespace-nowrap"
                >
                    {t('Account.Button.Back')}
                </Button>
                <Button
                    data-testid="register-submit"
                    type="submit"
                    onClick={doRegister}
                    isLoading={registerMutation.isPending}
                    disabled={!isHydrated}
                    variant="primary"
                    classNames="flex-1 whitespace-nowrap"
                >
                    {t('Account.Button.Register')}
                </Button>
            </div>
        </form>
    );
};
